/**
 * English copy for the eleven free tools.
 *
 * Every `answer` below is written to one specification: 134–167 words, leading
 * with the direct answer, and complete on its own — it has to make sense pasted
 * into a chat window with nothing around it, because that is exactly what
 * happens to it. `content.test.ts` enforces the length; the self-containment is
 * on whoever edits it.
 *
 * The second rule is honesty about what is being measured, and it is a product
 * position rather than a legal hedge. Every competitor returns a number from a
 * black box. The only thing a free browser tool has to trade on is showing its
 * working — so the copy says which convention a target comes from and how wide
 * the real spread is, and it never implies the number is a fact about the face
 * rather than a distance from an agreed reference.
 */

import type { ToolCopy, ToolSlug } from "./types";

export const en: Record<ToolSlug, ToolCopy> = {
  "looksmax-test": {
    title: "Free Looksmax Test — Score Your Face in Your Browser | Fazly",
    description:
      "Upload a front-facing photo and get a looksmax score built from eight measurements — symmetry, golden ratio, jawline, canthal tilt and more. Runs on your device; nothing is uploaded.",
    h1: "Free looksmax test",
    standfirst:
      "Eight measurements, one composite, and the arithmetic behind it shown in full.",
    answer:
      "A looksmax test scores a face by measuring the distances and angles between its features and comparing them against a set of agreed proportions. This one takes eight measurements from a single front-facing photo: bilateral symmetry, adherence to the golden ratio, jaw definition, canthal tilt, the balance of the facial thirds, facial width-to-height ratio, eye spacing and lip ratio. Each is scored from 0 to 100 against a stated target, then combined into one composite using published weights, with symmetry weighted heaviest because it is the measurement with the strongest link to how faces are actually rated. The whole thing runs inside your browser using Google's MediaPipe face mesh, so the photo never leaves your device and nothing is stored. The composite is a distance from a convention, not a fact about your face.",
    measures:
      "A weighted composite of eight facial measurements, scored 0 to 100.",
    reading: [
      "The composite is the weighted average of the eight scores below it. The weights are shown, so you can see which measurement moved it.",
      "A low score on one measurement rarely moves the composite much. Two or three together do.",
      "Symmetry carries the most weight because it is measured by comparing paired points, not against an invented target.",
      "Lighting and angle change the result. A flat, front-facing photo in even light is the only one worth comparing to another.",
    ],
    bands: [
      {
        from: 0,
        to: 50,
        label: "Below the convention",
        // This said "the most populated one". It is not: of the 2,168
        // successful looksmax scans in the thirty days to 2026-10-09, 82% had
        // a composite between 50 and 70 and 8% under 50 (`web_tool_scan.band`
        // in PostHog "Fazly Web"). Corrected in all five languages.
        note: "Most measurements sit well away from their reference. The widest band, though fewer photos land here than in the one above.",
      },
      {
        from: 50,
        to: 70,
        label: "Within range",
        note: "Proportions land inside the usual spread on most measurements.",
      },
      {
        from: 70,
        to: 85,
        label: "Above the convention",
        note: "Several measurements sit close to their reference at once.",
      },
      {
        from: 85,
        to: 101,
        label: "Near the reference",
        note: "Rare. Nearly every measurement lands near its target — worth checking the photo is flat and evenly lit before believing it.",
      },
    ],
    faq: [
      {
        q: "Is the looksmax test accurate?",
        a: "It is accurate about what it measures: the distances and angles between 478 detected facial landmarks. Whether those proportions mean anything about attractiveness is a separate question the test cannot answer, and the targets it scores against are conventions rather than findings.",
      },
      {
        q: "Does my photo get uploaded?",
        a: "No. The face mesh runs as WebAssembly inside your browser and the image never leaves the page. You can confirm it in your browser's network tab: there is no request carrying the photo, because there is nowhere for it to go.",
      },
      {
        q: "Why did my score change between two photos?",
        a: "Camera angle is the usual reason. Tilting your head, holding the phone above eye level, or a wide lens close to your face all change the measured ratios. The tool corrects for head roll but cannot correct for pitch or perspective.",
      },
    ],
    upsell:
      "The app tracks these eight over time, adds skin and hair, and turns the weakest one into a daily routine.",
  },

  "attractiveness-test": {
    title: "Free Attractiveness Test — How Attractive Am I? | Fazly",
    description:
      "Rate your face out of 10 from one photo: symmetry, proportions and six more measurements, scored in your browser. Free, no sign-up, and nothing is uploaded.",
    h1: "Free attractiveness test",
    standfirst:
      "A score out of 10 for what a photo can measure — and a straight answer about what it can't.",
    answer:
      "No photo can measure how attractive a face is: attractiveness is a judgment people make, and a test can only measure the geometry they are reacting to. On two points the research is clearer than the saying about the beholder suggests. People agree with one another about faces far more than you might expect, and symmetry is among the features most consistently linked to higher ratings — modestly, not decisively. This test measures what a single front-facing photo can show: eight proportions, including symmetry, the facial thirds, the golden ratio, jaw definition and eye spacing. Each is compared with a stated reference, and the eight are combined into a score out of 10. What it cannot see often matters more: skin, expression, grooming, hair and the photo itself, because lighting and camera distance change a face substantially. It runs in your browser, and the photo is never uploaded.",
    measures: "The eight-measurement composite, on a scale of 10.",
    reading: [
      "The score is the weighted average of the eight measurements below, divided by ten — the same composite the looksmax test reports out of 100.",
      "The two lists under the score name your highest and lowest measurements in this photo. They are the part worth acting on.",
      "A 6 is not a worse face than an 8. It is a face whose proportions sit further from one set of conventions.",
      "Photos taken differently cannot be compared: retake it in daylight, at eye level, with the phone at arm's length or further.",
    ],
    bands: [
      {
        from: 0,
        to: 5,
        label: "Further from the references",
        note: "Most of the eight measurements sit away from their reference. Fewer photos land here than in the band above, and lighting or angle is often part of the reason.",
      },
      {
        from: 5,
        to: 7,
        label: "Within the usual range",
        note: "Where most faces land: the majority of measurements inside the normal spread, a few outside it.",
      },
      {
        from: 7,
        to: 8.5,
        label: "Close to the references",
        note: "Several measurements sit near their reference at once. Uncommon, and still a statement about proportions rather than about how anyone sees you.",
      },
      {
        from: 8.5,
        to: 10.1,
        label: "Near every reference",
        note: "Rare. Check the photo is straight-on and evenly lit before believing it — angle can inflate a score as easily as it deflates one.",
      },
    ],
    faq: [
      {
        q: "Can a test really tell how attractive I am?",
        a: "No, and one that claims to is guessing. Attractiveness is a judgment, shaped by skin, expression, hair, grooming and who is looking. What a photo can measure is geometry, which is the part this test scores and shows in full.",
      },
      {
        q: "Is my photo uploaded or saved anywhere?",
        a: "No. The face mesh runs as WebAssembly inside your browser and the image never leaves the page. There is no account and nothing is stored, and you can confirm it in your browser's network tab while the test runs.",
      },
      {
        q: "Why do I get a different score in another photo?",
        a: "Because the camera changes the face. A phone held close makes the nose look wider and the face narrower, a tilted head moves every ratio, and harsh light hides landmarks. Compare only photos taken the same way.",
      },
    ],
    upsell:
      "The app reads what a photo test cannot — skin, hair and several angles of your face — and turns your weakest score into a daily routine.",
  },

  "face-shape": {
    title: "Face Shape Detector — What Is My Face Shape? | Fazly",
    description:
      "Find out whether your face is oval, round, square, heart, oblong or diamond — measured from your own photo, in your browser, with the four ratios that produced the answer shown.",
    h1: "What is my face shape?",
    standfirst:
      "Six shapes, four measurements, and the numbers that decided it.",
    answer:
      "Your face shape is determined by four ratios: face length divided by cheekbone width, jaw width divided by cheekbone width, forehead width divided by cheekbone width, and the angle at the corner of the jaw. An oval face is about one and a half times as long as it is wide with a jaw slightly narrower than the cheekbones. A round face is nearly as wide as it is long with soft angles. A square face has forehead, cheekbones and jaw within a few percent of each other and a sharp jaw angle. A heart face tapers from a wide forehead to a narrow chin. An oblong face is clearly longer than it is wide with straight sides. A diamond face is widest at the cheekbones with both forehead and jaw narrower. This tool measures all four ratios from your photo and shows them alongside the verdict.",
    measures: "Six-way classification from four measured facial ratios.",
    reading: [
      "The four ratios are shown under the verdict. If you disagree with the answer, the numbers are where to check.",
      "Confidence is the margin over the runner-up shape, not a probability. A low margin means your face sits between two shapes — which is common and not a fault.",
      "Hair covering the forehead moves the forehead-to-cheek ratio and can flip heart and diamond. Pull it back for a cleaner read.",
      "Face shape is a convention for choosing hairstyles and glasses. It is not a measurement of anything biological.",
      "Face height is estimated: the face mesh stops at the modelled forehead, not your hairline, so the full height is taken as 1.5x the glabella-to-chin distance.",
    ],
    bands: [],
    faq: [
      {
        q: "Which face shape is the most common?",
        a: "Oval and round together account for most faces in every published sample, which is partly why the two are the shapes people most often disagree about. The boundary between them is a single ratio — face length over cheekbone width — with no natural gap in it.",
      },
      {
        q: "Can my face shape change?",
        a: "The bone proportions do not change after your early twenties, but the measured shape can. Losing or gaining weight changes cheek and jaw width, and both are inputs here. So is a receding hairline, which lengthens the measured face.",
      },
      {
        q: "Why does another site give me a different shape?",
        a: "Because the thresholds are conventions and nobody agrees on them. A site that puts the oval/oblong boundary at 1.6 and one that puts it at 1.5 will disagree about every face in between. This tool shows its four ratios so you can see which side of the line you landed on.",
      },
    ],
    upsell:
      "The app pairs your shape with the hairstyle, beard line and glasses that suit its proportions.",
  },

  "face-symmetry": {
    title: "Face Symmetry Test — Measure Your Facial Symmetry | Fazly",
    description:
      "Measure how symmetric your face is from one photo. Twelve mirrored landmark pairs, compared against your own facial midline, scored in your browser with nothing uploaded.",
    h1: "How symmetric is my face?",
    standfirst: "Twelve mirrored pairs, measured against your own midline.",
    answer:
      "Facial symmetry is measured by comparing pairs of features on either side of the facial midline and totalling how far apart their positions are. This tool takes twelve pairs — the eye corners, eyelids, brows, cheekbones, temples, jaw corners, nostrils and mouth corners — and measures each one's distance from the midline running through your forehead, nose and chin. The differences are added up and normalised by the distance between your eyes, so the score does not depend on how close you stood to the camera. No face is symmetric. Published measurements put typical deviation well above zero for everyone, and perfect scores usually mean the photograph was flat and evenly lit rather than that the face was flawless. What the score is useful for is comparison against your own earlier photos, taken the same way.",
    measures:
      "Mean deviation of twelve mirrored landmark pairs, as a percentage.",
    reading: [
      "100% would mean every mirrored pair sits at an identical distance from the midline. Nobody scores that on an honest photo.",
      "The midline is derived from your forehead, nose and chin — not from the average of the pairs, which would hide a face whose whole left side is wider.",
      "Head rotation is the biggest source of error. Turning a few degrees to one side makes that side measure narrower.",
      "Compare against your own photos taken in the same light and pose. Comparing against someone else's score compares two cameras.",
    ],
    bands: [
      {
        from: 0,
        to: 55,
        label: "Visible asymmetry",
        note: "The difference between the two sides is large enough to see in the photo, not just in the numbers.",
      },
      {
        from: 55,
        to: 75,
        label: "Typical",
        note: "Where most honest front-facing photographs land.",
      },
      {
        from: 75,
        to: 90,
        label: "Above typical",
        note: "Paired features line up closely. Check the photo is square-on before reading much into it.",
      },
      {
        from: 90,
        to: 101,
        label: "Near-mirror",
        note: "Rare on a real photograph, and usually a sign of a very flat, very even shot.",
      },
    ],
    faq: [
      {
        q: "Is a symmetric face more attractive?",
        a: "Symmetry correlates with rated attractiveness in the published literature, but weakly, and the effect shrinks as photo quality improves. It is one input among many and it is not the reason any individual face is rated the way it is.",
      },
      {
        q: "Why is my symmetry score lower than I expected?",
        a: "Almost always the pose. A few degrees of head rotation compresses one side of the face in the image, and the tool measures the image. Retake the photo square-on to the lens, at eye level, with even light on both sides.",
      },
      {
        q: "Can facial asymmetry be fixed?",
        a: "Mild asymmetry is universal and nothing to fix. Where it comes from habit — chewing on one side, sleeping on one side, a persistent head tilt — it can shift over months. Structural asymmetry does not change without clinical intervention, which is out of this tool's scope.",
      },
    ],
    upsell:
      "The app scores symmetry on every scan, so the number becomes a line rather than a one-off.",
  },

  "golden-ratio": {
    title: "Golden Ratio Face Test — Measure Your Face Against Phi | Fazly",
    description:
      "Measure your face against the golden ratio (1.618) across four classical proportion checks — length to width, mouth to nose, and two more — in your browser, with nothing uploaded.",
    h1: "Does my face match the golden ratio?",
    standfirst: "Four classical proportion checks against phi, averaged.",
    answer:
      "The golden ratio face test compares facial proportions against phi, approximately 1.618, across several classical checks. This tool measures four: face length divided by cheekbone width, face length divided by three times the nose-to-chin distance, mouth width divided by nose width, and the nose-to-chin distance divided by nose length. Each is scored by how far it sits from 1.618 and the four are averaged, because any single ratio lands near phi for plenty of faces by coincidence. It is worth knowing what this measures. The idea that beautiful faces follow phi comes from Renaissance drawing convention and from a 1980s cosmetic-surgery mask, not from a finding — measured populations do not cluster at 1.618. A high score means your proportions match a drawing convention closely, which is interesting and is not the same as a fact about your face.",
    measures: "Mean adherence to phi across four classical facial proportions.",
    reading: [
      "The headline number is face length over cheekbone width. The score is the average across all four checks.",
      "1.618 is the target for every check, so a score of 100 would need all four to land on phi at once.",
      "Real faces spread roughly between 1.2 and 2.1 on the first check. Sitting outside phi is the normal case, not a defect.",
      "This is the tool on the site with the weakest evidence behind its target, and the copy says so on purpose.",
      "Face length is estimated the same way the shape detector estimates it — the mesh cannot see your hairline, so the height is 1.5x the glabella-to-chin distance.",
    ],
    bands: [
      {
        from: 0,
        to: 1.35,
        label: "Wide of phi",
        note: "Face is broad relative to its length compared with the classical convention.",
      },
      {
        from: 1.35,
        to: 1.5,
        label: "Below phi",
        note: "Common. Sits between the round and oval conventions.",
      },
      {
        from: 1.5,
        to: 1.72,
        label: "Near phi",
        note: "Within about 0.1 of 1.618 — the band the convention points at.",
      },
      {
        from: 1.72,
        to: 3,
        label: "Above phi",
        note: "Face is long relative to its width compared with the classical convention.",
      },
    ],
    faq: [
      {
        q: "Is the golden ratio actually linked to beauty?",
        a: "Not in the way it is usually presented. The claim traces to Renaissance drawing practice and to Marquardt's phi mask from the 1990s; attempts to reproduce a preference for phi proportions in measured populations have been mixed at best, and the mask itself has been criticised for encoding one ethnic norm.",
      },
      {
        q: "What counts as a good golden ratio score?",
        a: "There is no threshold worth defending. The four checks are averaged, so a score above 70 means most of your proportions land within about a tenth of phi. Whether that is good is a question about the convention, not about you.",
      },
      {
        q: "Why do golden-ratio calculators disagree?",
        a: "They measure different distances. Some use hairline to chin, others the top of the modelled forehead; some use jaw width rather than cheekbone width. Each choice moves the ratio by more than the differences people argue about, which is why this tool names the four it uses.",
      },
    ],
    upsell:
      "The app measures the same proportions on every scan and puts them next to the ones that respond to effort.",
  },

  "canthal-tilt": {
    title: "Canthal Tilt Test — Measure Your Eye Tilt in Degrees | Fazly",
    description:
      "Measure your canthal tilt in degrees from one photo. Positive, neutral or negative, measured separately for each eye, in your browser with nothing uploaded.",
    h1: "What is my canthal tilt?",
    standfirst: "Measured per eye, in degrees, after correcting for head roll.",
    answer:
      "Canthal tilt is the angle of the line from the inner corner of the eye to the outer corner, measured against the horizontal. A positive tilt means the outer corner sits higher than the inner one; a negative tilt means it sits lower. This tool measures each eye separately and reports both, along with the average. The oculoplastic literature puts the usual adult range between roughly two and eight degrees positive, with a slight difference between the two eyes being normal. Before measuring, the tool rotates the detected face so the eye line is level, because otherwise it would be measuring how you held the phone: a five-degree head tilt reads as five degrees of canthal tilt on both eyes at once. A negative measurement on a photo taken from below is usually the camera, not the eye — retake at eye level before drawing conclusions.",
    measures: "The inner-to-outer eye corner angle, in degrees, per eye.",
    reading: [
      "Positive means the outer corner is higher. Negative means it is lower.",
      "Each eye is reported separately. A degree or two of difference between them is normal.",
      "Camera height matters more than almost anything else here. Shooting from below flattens or reverses the tilt.",
      "The tool levels the face by the eye line before measuring, so head roll is corrected. Head pitch is not, and cannot be.",
    ],
    bands: [
      {
        from: -30,
        to: -1,
        label: "Negative tilt",
        note: "Outer corner sits below the inner corner. Check the camera was at eye level before reading further.",
      },
      {
        from: -1,
        to: 2,
        label: "Neutral",
        note: "The eye line is close to horizontal.",
      },
      {
        from: 2,
        to: 8,
        label: "Positive tilt",
        note: "The band most commonly reported as typical in adults.",
      },
      {
        from: 8,
        to: 40,
        label: "Strongly positive",
        note: "Above the usual adult range. Frequently an artefact of a low camera angle.",
      },
    ],
    faq: [
      {
        q: "What is a normal canthal tilt?",
        a: "Roughly two to eight degrees positive is the range most commonly reported for adults, with some variation by ancestry. Values outside it are not abnormal in any medical sense; they are outside the range this convention describes.",
      },
      {
        q: "Can canthal tilt be changed without surgery?",
        a: "The angle is set by where the tendons anchor to the orbital bone, so no exercise, massage or routine changes it. What can change is how it reads — under-eye puffiness and the position of the brow both affect the perceived tilt without touching the measured one.",
      },
      {
        q: "Why does my tilt look negative in photos but not in the mirror?",
        a: "Because a phone held below eye level, at arm's length, applies enough perspective distortion to flatten the outer corners. A mirror does not. Take the photo at eye level with the phone as far away as your arm allows and the two usually agree.",
      },
    ],
    upsell:
      "The app scores the eye area as one of its seven, alongside the rest of the face.",
  },

  jawline: {
    title: "Jawline Test — Measure Your Gonial Angle and Jaw Width | Fazly",
    description:
      "Measure your jaw definition from one photo: gonial angle in degrees and jaw-to-cheekbone width ratio, scored in your browser with nothing uploaded.",
    h1: "How defined is my jawline?",
    standfirst: "Gonial angle and jaw-to-cheek width, scored together.",
    answer:
      "Jaw definition is measured two ways at once, because either one alone is misleading. The first is the gonial angle: the angle at the corner of the jaw, between the line running up toward the ear and the line running forward to the chin. A smaller angle is a sharper jaw, and the adult range runs roughly from 110 to 130 degrees. The second is jaw width divided by cheekbone width, because a narrow face can produce a crisp angle while still reading as slight — the ratio is what separates a defined jaw from a small one, and around 0.8 is the middle of the band where the jaw carries the lower face without out-widening the cheekbones. This tool measures both and averages the two scores. Body fat around the jaw changes the measured width without changing the bone underneath it.",
    measures: "Jaw-to-cheekbone width ratio, scored with the gonial angle.",
    reading: [
      "The headline number is jaw width over cheekbone width. The gonial angle is shown next to it in degrees.",
      "A lower gonial angle means a sharper corner. Under about 120 degrees reads as defined.",
      "The two are averaged, not multiplied, so a sharp angle on a narrow jaw still scores in the middle rather than at zero.",
      "This is the measurement most affected by body composition and by the angle of the shot. Chin-down photos flatter it.",
    ],
    bands: [
      {
        from: 0,
        to: 0.66,
        label: "Narrow",
        note: "Jaw is considerably narrower than the cheekbones. Common on heart and diamond face shapes.",
      },
      {
        from: 0.66,
        to: 0.8,
        label: "Tapered",
        note: "The most common band — jaw narrower than the cheekbones but carrying the lower face.",
      },
      {
        from: 0.8,
        to: 0.95,
        label: "Defined",
        note: "Jaw close to cheekbone width, which is what reads as a strong lower third.",
      },
      {
        from: 0.95,
        to: 2,
        label: "Wide",
        note: "Jaw at or beyond cheekbone width. Characteristic of square face shapes.",
      },
    ],
    faq: [
      {
        q: "What is a good gonial angle?",
        a: "Around 120 degrees is the usual reference for a defined adult jaw, with the normal range spanning roughly 110 to 130. Lower is sharper. The number is measured from a front-facing photo here, which approximates an angle properly measured in profile.",
      },
      {
        q: "Does mewing change your jawline?",
        a: "Tongue posture cannot move adult bone. What the practice can plausibly affect is muscle tone and habitual posture, both of which change how the jaw reads without changing the gonial angle. Claims of adult bone remodelling from tongue position are not supported.",
      },
      {
        q: "Why does my jaw score better in some photos?",
        a: "Camera height and head pitch. A camera slightly above eye level with the chin lowered shortens the measured jaw width and sharpens the apparent angle. That is the standard flattering angle and it is why comparisons only work between photos taken the same way.",
      },
    ],
    upsell:
      "Jawline is one of the seven scores the app tracks, with a routine attached when it is your weakest.",
  },

  fwhr: {
    title: "fWHR Test — Facial Width-to-Height Ratio Calculator | Fazly",
    description:
      "Measure your facial width-to-height ratio (fWHR) from one photo — bizygomatic width over upper-face height, the way the research defines it. Runs in your browser.",
    h1: "What is my fWHR?",
    standfirst:
      "Cheekbone width over upper-face height — the definition the research uses.",
    answer:
      "Facial width-to-height ratio is cheekbone-to-cheekbone width divided by the height of the upper face, measured from the upper lip to the highest point of the eyelids. The upper-face part is what people most often get wrong when calculating it by hand: it is not the whole face, and using chin-to-brow instead produces a number around a third smaller that cannot be compared with any published figure. Measured populations centre near 1.9, with most faces falling between about 1.6 and 2.2. fWHR has a real research literature behind it, mostly on perceived dominance and on behaviour in competitive settings, and that literature is contested — several of the larger replications have found much weaker effects than the original studies. It is included here because it is a well-defined measurement with a stated definition, not because a high or low value means anything about you.",
    measures: "Bizygomatic width divided by upper-face height.",
    reading: [
      "Height is measured from the upper lip to the highest point of the eyelids — the upper face only, not to the chin.",
      "Most faces measure between 1.6 and 2.2. The population centre is near 1.9.",
      "Higher means a wider face relative to the upper third. Lower means a longer one.",
      "Eyelid position moves this number, so a wide-eyed expression measures lower than a relaxed one.",
    ],
    bands: [
      {
        from: 0,
        to: 1.6,
        label: "Below typical",
        note: "Upper face is tall relative to its width.",
      },
      {
        from: 1.6,
        to: 1.9,
        label: "Typical, lower half",
        note: "Inside the usual range, below the population centre.",
      },
      {
        from: 1.9,
        to: 2.2,
        label: "Typical, upper half",
        note: "Inside the usual range, above the population centre.",
      },
      {
        from: 2.2,
        to: 4,
        label: "Above typical",
        note: "Wide relative to the upper-face height. Check the photo is square-on.",
      },
    ],
    faq: [
      {
        q: "How is fWHR calculated?",
        a: "Width between the two zygions — the widest point of each cheekbone — divided by the vertical distance from the upper lip to the highest point of the eyelids. The upper-face height is the part that distinguishes it from a face length-to-width ratio.",
      },
      {
        q: "What does a high fWHR mean?",
        a: "In the research, a higher ratio is associated with judgements of dominance rather than with any trait a person has. The size of that association has shrunk considerably in larger replication attempts, so it is best read as a fact about how faces are perceived, not about the people behind them.",
      },
      {
        q: "Does fWHR change with weight?",
        a: "Yes, more than most facial measurements. Fat over the cheeks increases the measured width while the upper-face height barely moves, so the ratio rises with weight gain and falls with weight loss even though the underlying bone is unchanged.",
      },
    ],
    upsell:
      "The app's harmony score reads proportions like this one alongside the rest of the face.",
  },

  "facial-thirds": {
    title: "Facial Thirds Test — Are Your Face Proportions Balanced? | Fazly",
    description:
      "Measure the three horizontal thirds of your face — forehead, midface and lower third — and see how evenly they divide. Runs in your browser, nothing uploaded.",
    h1: "Are my facial thirds balanced?",
    standfirst:
      "Forehead, midface and lower third, as percentages that sum to 100.",
    answer:
      "The classical canon divides the face into three equal horizontal parts: hairline to brow, brow to the base of the nose, and the base of the nose to the bottom of the chin. This tool measures all three from your photo and reports each as a percentage of total face height, so they always sum to 100. The score is the ratio of the midface to the lower third, because those are the two the mesh can locate; the spread a viewer notices is dominated by them — a face where one third dominates reads as long-midfaced or short-chinned regardless of what the other two do. One honest caveat about the upper third: the face mesh stops at the top of the modelled forehead rather than at your actual hairline, so a receding or high hairline makes the upper third measure smaller than it is. The lower two are reliable.",
    measures: "Midface over lower face, with all three thirds reported.",
    reading: [
      "Each third is a percentage of total face height. Equal thirds would be 33.3 each.",
      "The score is the midface divided by the lower face, targeting 1.0 — the two thirds a photo can actually measure.",
      "The upper third is a proxy: it is measured to the top of the modelled forehead, not to your hairline.",
      "A long midface and a short lower third are the two most common imbalances, and neither is a defect.",
    ],
    bands: [
      {
        from: 0,
        to: 0.85,
        label: "Long lower face",
        note: "The lower third is noticeably longer than the midface. Common, and the most flattered by a camera held above eye level.",
      },
      {
        from: 0.85,
        to: 1.15,
        label: "Balanced",
        note: "Midface and lower face within about 15% of each other — what the canon asks for.",
      },
      {
        from: 1.15,
        to: 1.4,
        label: "Long midface",
        note: "The midface runs longer than the lower third. The most common imbalance of the two.",
      },
      {
        from: 1.4,
        to: 4,
        label: "Strongly long midface",
        note: "Well beyond the usual spread. Check the camera was at eye level: shooting from below foreshortens the lower third and inflates this.",
      },
    ],
    faq: [
      {
        q: "What are the facial thirds?",
        a: "Hairline to glabella (the flat patch between the brows), glabella to subnasale (where the nose meets the upper lip), and subnasale to the bottom of the chin. The convention, which goes back to Vitruvius by way of Leonardo, wants the three to be equal.",
      },
      {
        q: "Can facial thirds be changed?",
        a: "Not by anything non-surgical. The proportions are set by the skull. What does change is how they read — a hairstyle changes the apparent upper third, and posture and body fat change how much of the lower third is visible.",
      },
      {
        q: "Why is my upper third measured as smaller than it looks?",
        a: "The face mesh ends at the top of the modelled forehead, which sits below most hairlines. That makes the upper third systematically shorter than a measurement taken to the real hairline, and the effect is larger the higher your hairline is.",
      },
    ],
    upsell:
      "The app's harmony score reads all three thirds together with the rest of the proportions.",
  },

  "eye-spacing": {
    title: "Eye Spacing Test — Are Your Eyes Wide Set or Close Set? | Fazly",
    description:
      "Measure the distance between your eyes against the width of one eye — the classical one-eye-width rule — from your own photo, in your browser.",
    h1: "Are my eyes wide set or close set?",
    standfirst:
      "Intercanthal distance measured in eye-widths, against the classical rule.",
    answer:
      "Eye spacing is measured as the distance between the inner corners of the eyes divided by the width of one eye. The classical rule is that the two should be equal — the eyes sit one eye-width apart — so the target is 1.0 and the measurement is immediately legible: 1.2 means your eyes sit a fifth of an eye further apart than the convention, and 0.85 means they sit closer. Both are common. The measurement is taken after the face is levelled by the eye line, and it is normalised by eye width rather than by any absolute distance, so it does not change with how close you stood to the camera. Unlike most measurements on this site, this one barely moves with weight, expression or age, which makes it one of the more stable numbers here and a reasonable one to compare between photographs taken years apart.",
    measures: "Intercanthal distance divided by the width of one eye.",
    reading: [
      "1.0 is the classical rule: eyes one eye-width apart.",
      "Above 1.0 is wide-set, below is close-set. Most faces land between 0.85 and 1.2.",
      "The number is normalised by your own eye width, so camera distance does not affect it.",
      "This is the most stable measurement on the site — it barely moves with weight or expression.",
    ],
    bands: [
      {
        from: 0,
        to: 0.85,
        label: "Close set",
        note: "Inner corners sit closer than one eye-width apart.",
      },
      {
        from: 0.85,
        to: 1.05,
        label: "Classical",
        note: "Within the one-eye-width convention.",
      },
      {
        from: 1.05,
        to: 1.25,
        label: "Wide set",
        note: "A common variation, often read as a distinctive feature rather than a flaw.",
      },
      {
        from: 1.25,
        to: 3,
        label: "Strongly wide set",
        note: "Well beyond the convention. Check the photo is square-on to the lens.",
      },
    ],
    faq: [
      {
        q: "What is the ideal distance between the eyes?",
        a: "The convention is one eye-width, which is a drawing rule rather than a finding. Measured populations spread on either side of it, and neither direction is associated with anything in particular beyond how the face is described.",
      },
      {
        q: "Can eye spacing be changed?",
        a: "No. The distance is set by the orbital bones and does not respond to anything non-surgical. What changes the perception is the brow, the bridge of the nose and where hair sits — all of which affect how far apart the eyes look without moving them.",
      },
      {
        q: "Is wide-set or close-set better?",
        a: "Neither. Both appear across every population and both are common among faces rated highly. The measurement is included because it is well defined and stable, not because one end of it is preferable.",
      },
    ],
    upsell:
      "The app scores the eye area as a whole, spacing included, and tracks it across scans.",
  },

  "lip-ratio": {
    title: "Lip Ratio Test — Measure Your Upper to Lower Lip Ratio | Fazly",
    description:
      "Measure the ratio between your lower and upper lip height from one photo, against the classical 1:1.6 convention. Runs in your browser, nothing uploaded.",
    h1: "What is my lip ratio?",
    standfirst: "Lower lip height over upper lip height, against 1.618.",
    answer:
      "Lip ratio is the height of the lower lip divided by the height of the upper lip. The convention puts it near 1.6 — the same phi that runs through the rest of the classical proportion rules — meaning the lower lip is a little over half again as tall as the upper one. This tool measures both from the vermilion borders in your photo and reports the ratio. Two things are worth knowing before reading much into it. The first is that lip fullness has the widest genuine population spread of anything measured on this site, and varies substantially with ancestry, so the convention describes one narrow reference rather than a norm. The second is that expression dominates the measurement: a slight smile thins the upper lip disproportionately, and a relaxed closed mouth is the only pose where two photographs are comparable.",
    measures: "Lower lip height divided by upper lip height.",
    reading: [
      "1.618 is the classical target. Above it means a fuller lower lip relative to the upper.",
      "Keep the mouth relaxed and closed. A smile thins the upper lip and inflates the ratio.",
      "This measurement has the widest real spread on the site and varies substantially with ancestry.",
      "Lip height is measured at the midline, so an asymmetric mouth is not captured here — the symmetry tool is.",
    ],
    bands: [
      {
        from: 0,
        to: 1.1,
        label: "Balanced",
        note: "Upper and lower lip close to the same height.",
      },
      {
        from: 1.1,
        to: 1.45,
        label: "Below the convention",
        note: "Lower lip fuller than the upper, but less so than the classical rule.",
      },
      {
        from: 1.45,
        to: 1.8,
        label: "Classical",
        note: "Around the 1:1.6 convention.",
      },
      {
        from: 1.8,
        to: 4,
        label: "Above the convention",
        note: "Lower lip considerably fuller. Check the mouth was relaxed and closed.",
      },
    ],
    faq: [
      {
        q: "What is the ideal lip ratio?",
        a: "The classical answer is 1:1.6, lower to upper. It comes from the same phi convention as the rest of the golden-ratio rules and has the same weak evidential basis — it describes one aesthetic reference, not a measured preference.",
      },
      {
        q: "Why does my lip ratio change between photos?",
        a: "Expression. Even a slight smile stretches and thins the upper lip while the lower one changes less, which pushes the ratio up. Photographs are only comparable when the mouth is relaxed and closed in both.",
      },
      {
        q: "Can lip ratio be improved naturally?",
        a: "Not meaningfully. Hydration and lip care affect how full lips look at the margins, but the vermilion height is anatomical. Anything that changes it materially is a clinical procedure and outside what this tool or the app addresses.",
      },
    ],
    upsell:
      "The app reads lips as part of its harmony score rather than as a number on their own.",
  },
};
